// Members tab: mirrors ClubMembersTab + ClubDetailsScreen role change (:744-777).
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClubService } from "../../../../services/clubService";
import { isClubLeaderRole } from "../../../../utils/notificationTargeting";
import { useToast } from "../Toast";
import Modal, { ModalBody, ModalFooter } from "../Modal";
import { Avatar, Button, Card, Empty, SkeletonRows } from "../primitives";
import { msg } from "./clubUtils";
import { ROLE_CLASS, ROLE_LABEL } from "../../lib/roles";
import { userPath } from "../../lib/paths";


export default function ClubMembers({ clubId, members, loading, uid, isAdmin, isStrict, onRoleChanged }) {
  const toast = useToast();
  const [target, setTarget] = useState(null); // member awaiting role-change confirmation
  const [saving, setSaving] = useState(false);
  const clubIdRef = useRef(clubId);
  clubIdRef.current = clubId;

  if (loading) return <SkeletonRows rows={5} />;
  if (!members.length) return <Empty>No members yet.</Empty>;

  const sorted = [...members].sort((a, b) => Number(isClubLeaderRole(b.role)) - Number(isClubLeaderRole(a.role)));
  const newRole = target ? ((target.role || "member") === "e-board" ? "member" : "e-board") : null;

  const confirm = async () => {
    if (!target || !isStrict) return;
    setSaving(true);
    const startedFor = clubId;
    const memberId = target.user_id || target.id;
    const { error } = await ClubService.updateMemberRole(clubId, memberId, newRole);
    setSaving(false);
    if (clubIdRef.current !== startedFor) { setTarget(null); return; } // route moved to another club
    if (error) { toast(msg(error, "Failed to update role"), "alert"); return; }
    toast(`Role updated to ${newRole}`);
    setTarget(null);
    onRoleChanged();
  };

  return (
    <>
      <Card>
        {sorted.map((m) => {
          const u = m.user || {};
          const role = m.role || "member";
          const memberUid = m.user_id || u.id;
          const mine = memberUid === uid;
          // Own row goes to /profile; others to the Profile section's /users/:userId.
          const to = memberUid ? (mine ? "/profile" : userPath(memberUid)) : null;
          const name = `${u.full_name || "Unknown Member"}${mine ? " (you)" : ""}`;
          const sub = [u.major, u.graduation_year].filter(Boolean).join(" · ");
          return (
            <div className="listrow" key={m.id || m.user_id}>
              {to ? (
                <Link to={to} tabIndex={-1} aria-hidden="true" data-testid={`clubDetails.member.${memberUid}.avatar`}>
                  <Avatar name={u.full_name} url={u.avatar_url} id={memberUid} size={42} />
                </Link>
              ) : <Avatar name={u.full_name} url={u.avatar_url} id={memberUid} size={42} />}
              <div className="grow" style={{ minWidth: 0 }}>
                <b>{to ? <Link to={to} style={{ color: "inherit" }} data-testid={`clubDetails.member.${memberUid}`}>{name}</Link> : name}</b>
                {sub && <div className="meta">{sub}</div>}
                {isAdmin && u.email && <div className="meta ellip">{u.email}</div>}
              </div>
              <span className={`role ${ROLE_CLASS[role] || "r-member"}`}>{ROLE_LABEL[role] || "Member"}</span>
              {isStrict && !mine && role !== "admin" && (
                <Button variant="sec" size="xs" onClick={() => setTarget(m)} data-testid={`clubDetails.member.${memberUid}.role`}>{role === "e-board" ? "Demote" : "Make E-Board"}</Button>
              )}
            </div>
          );
        })}
      </Card>
      {target && (
        <Modal title="Member actions" size="narrow" lock={saving} onClose={() => setTarget(null)} testId="clubDetails.roleConfirm">
          <ModalBody>
            <p className="muted">
              {newRole === "e-board" ? "Promote" : "Demote"} <b>{target.user?.full_name || "this member"}</b> {newRole === "e-board" ? "to E-Board?" : "to Member?"}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" disabled={saving} onClick={() => setTarget(null)} data-testid="clubDetails.roleConfirm.cancel">Cancel</Button>
            <Button disabled={saving} onClick={confirm} data-testid="clubDetails.roleConfirm.confirm">{newRole === "e-board" ? "Promote to E-Board" : "Demote to Member"}</Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
